import React, { Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import qs from 'query-string';
import { useLocation, useHistory } from 'react-router-dom';
import ReactTooltip from 'react-tooltip';
import pluralize from 'pluralize';

import useFilteredUngroupedPhotos from './hooks/useFilteredUngroupedPhotos';
import {
  ALBUM_PHOTO_FILTERS,
  ALBUM_PHOTO_STATUS,
} from '../../../../../../../../../../constants/albums';
import { capitalize } from '../../../../../../../../../../util/text';
import PhotoGrid from '../../../../../../../../../../components/grid/PhotoGrid';
import Photo from './components/Photo';
import AlbumPhotoMenu from '../../../../../../../../../../components/global/AlbumPhotoMenu';
import useUngroupedPhotoActions from './hooks/useUngroupedPhotoActions';
import { getErrorString } from '../../../../../../../../../../util/normalize-errors';
import useOpenAlert from '../../../../../../../../../../hooks/useOpenAlert';
import ReviewPhoto from './components/ReviewPhoto';
import UploadIcon from '../../../../../../../../../../components/icons/UploadIcon';
import Button from '../../../../../../../../../../components/global/Button';
import useAlbumSlideshowForPendingLayouts from '../../../../../hooks/useAlbumSlideshowForPendingLayouts';
import AlbumReviewPhotoViewerActions from '../../../../../../AlbumPhotosReview/components/AlbumReviewPhotoViewerActions/AlbumReviewPhotoViewerActions';
import SlideshowDesignerPhotoViewerActions from './components/SlideshowDesignerPhotoViewerActions';
import {
  isNativeAppWebView,
  postNativeAppAlbumSlideshowDesignerUploadPhotos,
} from '../../../../../../../../../../services/native-bridge/native-apps-interface';

const propTypes = {
  albumId: PropTypes.string.isRequired,
  defaultAlbumPhotoFilter: PropTypes.oneOf(Object.values(ALBUM_PHOTO_FILTERS)),
};

const PhotosStep = ({ albumId, defaultAlbumPhotoFilter }) => {
  const openAlert = useOpenAlert();
  const location = useLocation();
  const history = useHistory();
  const {
    album,
    photos,
    photosTotal,
    loadMore,
    uploaderIds,
    albumPhotoFilter,
    onAlbumPhotosFilterUpdate,
  } = useFilteredUngroupedPhotos({ albumId });
  const isNativeAppWebViewBool = isNativeAppWebView();
  const {
    onAlbumPhotosDelete,
    onAlbumPhotosPublish,
    onAlbumPhotosHide,
  } = useUngroupedPhotoActions({ album });
  const { hasPendingLayouts } = useAlbumSlideshowForPendingLayouts({
    albumId,
  });
  useEffect(() => {
    if (defaultAlbumPhotoFilter) {
      onAlbumPhotosFilterUpdate({ albumPhotoFilter: defaultAlbumPhotoFilter });
    }
  }, [defaultAlbumPhotoFilter]);

  const onPhotoVisibilityToggle = async photo => {
    try {
      if (photo.album_photo_status === ALBUM_PHOTO_STATUS.PUBLISHED) {
        await onAlbumPhotosHide({
          albumId,
          photoIds: {
            select_all: false,
            selected_ids: [photo.id],
          },
          albumPhotoFilter,
          uploaderIds,
        });
      } else {
        await onAlbumPhotosPublish({
          albumId,
          photoIds: {
            select_all: false,
            selected_ids: [photo.id],
          },
          albumPhotoFilter,
          uploaderIds,
        });
      }
    } catch (e) {
      openAlert({ message: getErrorString(e) });
    }
  };

  const onPhotoDelete = async photo => {
    try {
      await onAlbumPhotosDelete({
        albumId,
        photoIds: {
          select_all: false,
          selected_ids: [photo.id],
        },
        albumPhotoFilter,
        uploaderIds,
      });
    } catch (e) {
      openAlert({ message: getErrorString(e) });
    }
  };

  return (
    <Fragment>
      <div className="d-flex w-100 align-items-center mb-1">
        <select
          className="secondary-photos-view-photos-step-selector"
          value={albumPhotoFilter}
          onChange={({ target }) =>
            onAlbumPhotosFilterUpdate({ albumPhotoFilter: target.value })
          }
        >
          {[
            ALBUM_PHOTO_FILTERS.ALL,
            ALBUM_PHOTO_FILTERS.PUBLISHED,
            ALBUM_PHOTO_FILTERS.REVIEWING,
            ALBUM_PHOTO_FILTERS.HIDDEN,
          ].map(filter => (
            <option key={filter} value={filter}>
              {capitalize(filter)}
            </option>
          ))}
        </select>
        {album?.photos_for_review?.total > 0 && (
          <Fragment>
            <Button
              pill
              className="ml-1"
              btnStyle="danger"
              btnSize="xs"
              data-tip=""
              data-for="review-photos-tip"
              data-effect="solid"
              data-place="bottom"
              onClick={() =>
                onAlbumPhotosFilterUpdate({
                  albumPhotoFilter: ALBUM_PHOTO_FILTERS.REVIEWING,
                })
              }
            >
              {album?.photos_for_review?.total}
            </Button>
            <ReactTooltip id="review-photos-tip" className="tooltip">
              Review {pluralize('photo', album?.photos_for_review?.total, true)}
            </ReactTooltip>
          </Fragment>
        )}
      </div>
      <Button
        btnStyle="reset"
        className="secondary-photos-view-photos-step-upload"
        onClick={() => {
          if (isNativeAppWebViewBool) {
            postNativeAppAlbumSlideshowDesignerUploadPhotos(albumId);
          } else {
            history.push({
              pathname: `/contributors/upload/${albumId}`,
              search: `?${qs.stringify({
                next: location.pathname,
              })}`,
            });
          }
        }}
      >
        <UploadIcon size={12} color="#0d84fe" className="mr-1" /> Upload photos
      </Button>
      <div className="secondary-photos-view-photos-step-photos">
        {photos.length > 0 && (
          <Fragment>
            {hasPendingLayouts && (
              <p className="text-light text-xs mt-1">
                Some photos are still being processed into slides&hellip;
              </p>
            )}

            <PhotoGrid
              records={photos}
              total={photosTotal}
              loadMore={loadMore}
              nativePhotoViewer
              forcedRecordRatio={1}
              targetRowHeight={118}
              defaultContainerWidth={240}
              photoViewerProps={{
                actions: ({ record: photo, onNavigateNextAfterRemoval }) => {
                  const isReviewing =
                    photo.album_photo_status === ALBUM_PHOTO_STATUS.REVIEWING;

                  if (!isReviewing) {
                    return (
                      <SlideshowDesignerPhotoViewerActions
                        photo={photo}
                        album={album}
                        onDelete={async () => {
                          await onPhotoDelete(photo);
                          onNavigateNextAfterRemoval();
                        }}
                        onVisibilityToggle={async () => {
                          await onPhotoVisibilityToggle(photo);
                          if (
                            [
                              ALBUM_PHOTO_STATUS.PUBLISHED,
                              ALBUM_PHOTO_FILTERS.HIDDEN,
                            ].includes(albumPhotoFilter)
                          ) {
                            onNavigateNextAfterRemoval();
                          }
                        }}
                      />
                    );
                  }

                  return (
                    <AlbumReviewPhotoViewerActions
                      photo={photo}
                      onDelete={async () => {
                        await onPhotoDelete(photo);
                        onNavigateNextAfterRemoval();
                      }}
                      onPublish={async () => {
                        await onPhotoVisibilityToggle(photo);
                        if (
                          albumPhotoFilter === ALBUM_PHOTO_FILTERS.REVIEWING
                        ) {
                          onNavigateNextAfterRemoval();
                        }
                      }}
                    />
                  );
                },
              }}
              cellRenderer={({ record: photo, recordPath }) => {
                const isHidden =
                  photo.album_photo_status !== ALBUM_PHOTO_STATUS.PUBLISHED;
                const isReviewing =
                  photo.album_photo_status === ALBUM_PHOTO_STATUS.REVIEWING;

                if (isReviewing) {
                  return (
                    <ReviewPhoto
                      photo={photo}
                      linkTo={recordPath}
                      onDelete={() => onPhotoDelete(photo)}
                      onPublish={() => onPhotoVisibilityToggle(photo)}
                    >
                      <AlbumPhotoMenu
                        album={album}
                        photo={photo}
                        downloadEnabled
                        withOverlay={false}
                        shareActionEnabled={false}
                        coverPhotoActionEnabled={false}
                        onDelete={() => onPhotoDelete(photo)}
                        onVisibilityToggle={() =>
                          onPhotoVisibilityToggle(photo)
                        }
                        isHidden={isHidden}
                        isReviewing={isReviewing}
                      />
                    </ReviewPhoto>
                  );
                }

                return (
                  <Photo
                    photo={photo}
                    albumId={albumId}
                    linkTo={recordPath}
                    isHidden={isHidden}
                    isReviewing={isReviewing}
                  >
                    <AlbumPhotoMenu
                      album={album}
                      photo={photo}
                      downloadEnabled
                      withOverlay={false}
                      shareActionEnabled={false}
                      coverPhotoActionEnabled={false}
                      onDelete={() => onPhotoDelete(photo)}
                      onVisibilityToggle={() => onPhotoVisibilityToggle(photo)}
                      isHidden={isHidden}
                      isReviewing={isReviewing}
                    />
                  </Photo>
                );
              }}
            />
          </Fragment>
        )}
      </div>
    </Fragment>
  );
};

PhotosStep.propTypes = propTypes;

export default PhotosStep;
